import { computed, toValue, watch, type MaybeRefOrGetter } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { loadStudioView } from '~/data/studio';
import { isNotFoundError } from '~/utils/apiErrors';

/**
 * Whether a view still reports work in flight, and therefore has to be read again.
 *
 * A projection says so in one of exactly two ways, and the rule reads both of its own
 * vocabulary rather than any one view's keys: a `status`/`state` that names a stage work
 * passes through, or a `running` count of the work the view itself holds. The count is what
 * a summary carries when the work behind it has no row on this view at all — documents being
 * read raise the header figure without putting a word anywhere in the response, so the
 * header used to grow and then stand still until the customer navigated (W8-UI-05 T1).
 */
export function inFlight(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  if (Array.isArray(value)) return value.some(inFlight);
  const record = value as Record<string, unknown>;
  const state = record.status ?? record.state;
  if (typeof state === 'string' && /^(queued|running|training|writing|preparing|in progress|reading|checking|creating|reindexing|waiting|evaluating|uploading)/i.test(state)) return true;
  if (typeof record.running === 'number' && record.running > 0) return true;
  return Object.values(record).some(inFlight);
}

/** The new templates consume view projections of the existing typed Go contract. */
export function useStudioData<T>(path: MaybeRefOrGetter<string>, options: {
  query?: Record<string, MaybeRefOrGetter<string | number>>;
  /**
   * How often to read again while nothing on the view is in flight, in milliseconds.
   *
   * A view whose facts are written by something outside the portal has no state that says
   * "work in flight", so the rule above never fires and the page stands still: an access key's
   * "Last used" did not move until the page was reloaded, and a customer checking whether their
   * call had reached us read the old value and concluded it had not (W8-UI-10). TanStack does
   * not poll a background tab, so this costs nothing while nobody is looking.
   */
  refetchInterval?: number;
} = {}) {
  const client = useQueryClient();
  const session = useSession();
  const { currentProjectId, settled: projectsSettled } = useProjectContext();
  const org = computed(() => session.value.data?.session.activeOrganizationId ?? null);
  const parameters = computed(() => Object.fromEntries(Object.entries(options.query ?? {}).map(([key,input]) => [key, toValue(input)])));
  const query = useQuery({
    queryKey: computed(() => ['studio', org.value, currentProjectId.value, toValue(path), parameters.value]),
    queryFn: async () => await loadStudioView({
      projectId: currentProjectId.value,
      query: parameters.value,
      get: <R>(resource: string) => client.fetchQuery({ queryKey: ['studio-source', org.value, resource], queryFn: () => apiFetch<R>(resource), staleTime: 5000 }),
    }, toValue(path)) as T,
    /* A project-scoped view is read only once the project list has answered: a null project then means "none", never "not loaded yet". */
    enabled: computed(() => (Boolean(session.value.data) && projectsSettled.value) || toValue(path).startsWith('/api/invitations/')),
    staleTime: 5000,
    refetchInterval: (query) => inFlight(query.state.data) ? 5000 : options.refetchInterval ?? false,
    retry: false,
  });
  const readError = useState<string | null>('studio-read-error', () => null);
  watch(query.error, (error) => { if (error) readError.value = error.message; });
  /* A read the API refused because the entity is not there is not an error the page recovers
     from by drawing itself emptier: the page says the entity does not exist and offers no
     action on it (W8-UI-07). */
  const missing = computed(() => isNotFoundError(query.error.value));
  return { data: query.data, pending: query.isPending, error: query.error, missing, refresh: query.refetch };
}
